import { FormControl, FormField, FormItem, FormLabel } from "@/src/components/ui/form";
import { emptyYachtAmenity, YachtAmenity, YachtForm } from "./yacht.schema";
import { useEffect, useState } from "react";
import { RadioGroup, RadioGroupItem } from "@/src/components/ui/radio-group";
import { Input } from "@/src/components/ui/input";
import { Switch } from "@/src/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/ui/select";
import { InputNumber } from "@/src/components/ui/input-number";
import { EllipsisVerticalIcon, PlusIcon } from "lucide-react";
import { Button } from "@/src/components/ui/button";
import { useFormContext } from "react-hook-form";
import { Popover, PopoverContent, PopoverTrigger } from "@/src/components/ui/popover";
import { Card } from "@/src/components/wrappers/Card";
import { CurrencySelect } from "@/src/components/CurrencySelect";
import { evalExtraPriceSimplified } from "@/src/app/Offers/EditOffer/YachtDetailsModal";
import { YachtDetails } from "../model";
import { useLoaderData } from "react-router-dom";
import { fetch } from "@/src/lib/fetch";
import { fetchAvailability, Availability } from "../../Offers/EditOffer/useOldSearch";

export function Amenities() {
  const [amenity, setAmenity] = useState<YachtAmenity>(emptyYachtAmenity);
  const form = useFormContext<YachtForm>();
  const amenities = form.watch("amenities") as YachtAmenity[];
  const equipment = amenities.filter((a) => a.included === true);
  const extras = amenities.filter((a) => a.included === false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const yacht = useLoaderData() as YachtDetails;
  const [availability, setAvailability] = useState<Availability[]>([]);
  const isEquipment = amenity.type === "equipment";

  const [, _setT] = useState(false);
  const rerender = () => _setT((t) => !t);

  const location = yacht?.location.names;
  const clientPrice = 0;

  useEffect(() => {
    async function getAvailability() {
      setAvailability([]);
      if (yacht?.id) {
        return fetchAvailability(yacht?.id).then(setAvailability);
      }
    }
    if (yacht?.id) {
      getAvailability();
    }
  }, []);

  useEffect(() => {
    if (amenity.type === "equipment") {
      setAmenity({
        ...amenity,
        price: null,
      });
    }
  }, [amenity.type]);

  return (
    <Card>
      <div className="flex flex-col items-stretch">
        <h3 className="text-lg font-semibold mb-4 ">Amenities</h3>
        <h5 className="text-sm mb-6">Select yacht’s extra services and equipment.</h5>
        <div className="flex gap-6 items-stretch">
          <div className="flex flex-col flex-1 gap-6">
            <RadioGroup value={amenity.type}>
              <div className="flex gap-4 items-center">
                <RadioGroupItem
                  onClick={() =>
                    setAmenity({
                      ...amenity,
                      type: "equipment",
                      included: true,
                    })
                  }
                  id="amenity_radio_eq"
                  value="equipment"
                />
                <label htmlFor="amenity_radio_eq" className="text-sm font-semibold">
                  Equipment
                </label>
              </div>
              <div className="flex gap-4 items-center">
                <RadioGroupItem
                  id="amenity_radio_extra"
                  value="extra"
                  onClick={() => {
                    setAmenity({
                      ...amenity,
                      type: "extra",
                      included: false,
                    });
                  }}
                />
                <label htmlFor="amenity_radio_extra" className="text-sm font-semibold">
                  Extra
                </label>
              </div>
            </RadioGroup>
            <FormField
              name="amenities"
              render={({ field }) => {
                return (
                  <FormItem required>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input
                        className="max-w-[500px]"
                        value={amenity.name}
                        onChange={(ev) => {
                          const text = ev.target.value;
                          setAmenity({
                            ...amenity,
                            name: text,
                          });
                        }}
                      />
                    </FormControl>
                  </FormItem>
                );
              }}
            />
            <div className="flex gap-4 items-center">
              <Switch
                id="amenity_obligatory_toggle"
                checked={amenity.obligatory}
                onCheckedChange={() =>
                  setAmenity({
                    ...amenity,
                    obligatory: !amenity.obligatory,
                  })
                }
              />
              <label htmlFor="amenity_obligatory_toggle" className="text-sm font-normal">
                Obligatory
              </label>
            </div>
            <div className="flex gap-4 items-center">
              <Switch
                disabled={isEquipment}
                id="amenity_price_toggle"
                checked={!!amenity.price}
                onCheckedChange={(checked) => {
                  if (checked) {
                    setAmenity({
                      ...amenity,
                      price: {
                        price: 0,
                        currency: "USD",
                        where: "in_advance",
                        per: "charter",
                      },
                    });
                  } else {
                    setAmenity({
                      ...amenity,
                      price: null,
                    });
                  }
                }}
              />
              <label htmlFor="amenity_price_toggle" className="text-sm font-normal">
                Paid
              </label>
            </div>
            {amenity.price && (
              <>
                <div className="grid grid-cols-4 gap-4">
                  <FormItem required className="col-span-2">
                    <FormLabel>Base price</FormLabel>
                    <FormControl>
                      <InputNumber
                        value={amenity.price?.price}
                        onChange={(n) =>
                          setAmenity({
                            ...amenity,
                            price: {
                              ...amenity.price,
                              price: n,
                            } as any,
                          })
                        }
                      />
                    </FormControl>
                  </FormItem>
                  <FormItem>
                    <FormLabel>&nbsp;</FormLabel>
                    <FormControl>
                      <CurrencySelect
                        onChange={(v) =>
                          setAmenity({
                            ...amenity,
                            price: {
                              ...amenity.price,
                              currency: v,
                            } as any,
                          })
                        }
                        value={amenity.price?.currency}
                      />
                    </FormControl>
                  </FormItem>
                  <FormItem>
                    <FormLabel>Per</FormLabel>
                    <Select
                      onValueChange={(v) =>
                        setAmenity({
                          ...amenity,
                          price: {
                            ...amenity.price,
                            per: v,
                          } as any,
                        })
                      }
                      defaultValue={amenity.price?.per}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="day">Day</SelectItem>
                        <SelectItem value="week">Week</SelectItem>
                        <SelectItem value="charter">Charter</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormItem>
                </div>
                <RadioGroup value={amenity.price?.where} required>
                  <div className="flex gap-4 items-center">
                    <RadioGroupItem
                      onClick={() =>
                        setAmenity({
                          ...amenity,
                          price: {
                            ...amenity.price,
                            where: "in_advance",
                          } as any,
                        })
                      }
                      id="amenity_where_in_adv"
                      value="in_advance"
                    />
                    <label htmlFor="amenity_where_in_adv" className="text-sm font-semibold">
                      In advance
                    </label>
                  </div>
                  <div className="flex gap-4 items-center">
                    <RadioGroupItem
                      id="amenity_where_locally"
                      value="locally"
                      onClick={() => {
                        setAmenity({
                          ...amenity,
                          price: {
                            ...amenity.price,
                            where: "locally",
                          } as any,
                        });
                      }}
                    />
                    <label htmlFor="amenity_where_locally" className="text-sm font-semibold">
                      Locally
                    </label>
                  </div>
                </RadioGroup>
              </>
            )}
            <Button
              type="button"
              variant="secondary"
              onClick={async () => {
                const updated = [...amenities];
                const newAmenity = {
                  ...amenity,
                  formula: parseToFormula(amenity.price?.per, amenity.price?.price),
                };
                const isValidName = !!newAmenity?.name?.length;
                if (!isValidName) {
                  form.setError("amenities", { message: "Required" });
                }
                if (isValidName) {
                  updated.push(newAmenity);
                  form.setValue("amenities", updated, { shouldValidate: true, shouldTouch: true });
                  setAmenity(emptyYachtAmenity);
                  setEditingIndex(null);
                }
              }}
            >
              {editingIndex !== null ? "Update amenity" : "Add to the yacht"} <PlusIcon size={16} />
            </Button>
          </div>

          <div className="w-0 border" />

          <div className="flex flex-col flex-1 max-w-80 gap-6 overflow-y-auto max-h-96">
            <h3 className="text-xl"> Amenities added ({amenities.length})</h3>
            {!amenities.length && (
              <p>You haven't added any amenities yet. Once you add an amenity to the yacht, it will appear here.</p>
            )}
            {!!equipment.length && (
              <div className="flex flex-col gap-4">
                <h4 className="text-l">Equipment</h4>
                {equipment.map((a) => {
                  const actualIndex = amenities.findIndex((am) => am === a);
                  return (
                    <div
                      key={actualIndex}
                      className="flex flex-col p-4 border border-blue-100 bg-slate-50 rounded-sm items-stretch"
                    >
                      <div className="flex gap-2 items-center">
                        <span>{a.name}</span>
                        {a.obligatory && <span className="text-sm bg-blue-100 rounded-3xl py-1 px-2">Obligatory</span>}
                        {!a.id && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button type="button" className="ml-auto py-2 px-3" variant="ghost">
                                <EllipsisVerticalIcon size={16} />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="p-0">
                              <div className="flex w-full flex-col rounded-md bg-popover text-popover-foreground">
                                <Button
                                  type="button"
                                  variant="destructive"
                                  onClick={() => {
                                    form.setValue(
                                      "amenities",
                                      amenities.filter((_, index) => index !== actualIndex),
                                    );
                                    rerender();
                                  }}
                                >
                                  Remove amenity
                                </Button>
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                      {a.formula && (
                        <>
                          <span className="font-light text-sm mt-1">
                            + {evalExtraPriceSimplified(a.formula, location, clientPrice)}
                          </span>
                          <span className="font-light text-sm">
                            {!a.paid_locally ? "paid in advance" : "paid locally"}
                          </span>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {!!extras.length && (
              <div className="flex flex-col gap-4">
                <h4 className="text-l">Extra services</h4>
                {extras.map((a) => {
                  const actualIndex = amenities.findIndex((am) => am === a);
                  return (
                    <div
                      key={actualIndex}
                      className="flex flex-col p-4 border border-blue-100 bg-slate-50 rounded-sm items-stretch"
                    >
                      <div className="flex gap-2 items-center">
                        <span>{a.name}</span>
                        {a.obligatory && <span className="text-sm bg-blue-100 rounded-3xl py-1 px-2">Obligatory</span>}
                        {!a.id && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button type="button" className="ml-auto py-2 px-3" variant="ghost">
                                <EllipsisVerticalIcon size={16} />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="p-0">
                              <div className="flex w-full flex-col rounded-md bg-popover text-popover-foreground">
                                <Button
                                  type="button"
                                  variant="destructive"
                                  onClick={() => {
                                    form.setValue(
                                      "amenities",
                                      amenities.filter((_, index) => index !== actualIndex),
                                    );
                                    rerender();
                                  }}
                                >
                                  Remove amenity
                                </Button>
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                      {a.formula && (
                        <>
                          <span className="font-light text-sm mt-1">
                            + {evalExtraPriceSimplified(a.formula, location, clientPrice)}
                          </span>
                          <span className="font-light text-sm">
                            {!a.paid_locally ? "paid in advance" : "paid locally"}
                          </span>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function parseToFormula(per?: string, price?: number) {
  if (!per || !price) {
    return "";
  }
  console.log("function: ", per, price);
  switch (per) {
    case "day":
      return `return ${price} * duration`;
    case "week":
      return `return ${price} * Math.ceil(duration/7)`;
    default:
      return `return ${price}`;
  }
}
